<script setup lang="ts">
import {
  ArrowUpRight,
  Braces,
  Check,
  Clipboard,
  Code2,
  ExternalLink,
  Layers3,
  TerminalSquare,
} from "lucide-vue-next"
import { computed, ref } from "vue"

import AppSelect from "../components/AppSelect.vue"
import { useApiKeysStore } from "../stores/apiKeys"

type McpScope = "all" | "rednote" | "zhihu" | "tophub" | "douban" | "douyin" | "weibo" | "weibo_web" | "smzdm" | "bilibili" | "v2ex" | "xueqiu" | "reddit" | "twitter" | "xianyu" | "jd" | "1688" | "nowcoder" | "instagram" | "maimai" | "jianyu" | "toutiao" | "sinafinance" | "juejin" | "36kr" | "dongchedi" | "ctrip" | "linkedin" | "github" | "google" | "imdb"
type PlatformScope = Exclude<McpScope, "all">

const apiKeys = useApiKeysStore()
const activeTab = ref<"mcp" | "rest">("mcp")
const mcpScope = ref<McpScope>("all")
const activePlatformScope = ref<PlatformScope>("rednote")
const copied = ref(false)
const key = computed(() => apiKeys.revealedKey?.plaintext ?? "<YOUR_API_KEY>")

const mcpEndpointOptions = [
  { scope: "all", name: "byrecc", label: "全部平台", url: "https://api.byre.cc/mcp" },
  { scope: "rednote", name: "byrecc-rednote", label: "仅小红书", url: "https://api.byre.cc/mcp/rednote" },
  { scope: "zhihu", name: "byrecc-zhihu", label: "仅知乎", url: "https://api.byre.cc/mcp/zhihu" },
  { scope: "tophub", name: "byrecc-tophub", label: "仅 TopHub", url: "https://api.byre.cc/mcp/tophub" },
  { scope: "douban", name: "byrecc-douban", label: "仅豆瓣", url: "https://api.byre.cc/mcp/douban" },
  { scope: "weibo", name: "byrecc-weibo", label: "仅微博 CLI", url: "https://api.byre.cc/mcp/weibo" },
  { scope: "weibo_web", name: "byrecc-weibo-web", label: "仅微博 Web", url: "https://api.byre.cc/mcp/weibo-web" },
  { scope: "smzdm", name: "byrecc-smzdm", label: "仅什么值得买", url: "https://api.byre.cc/mcp/smzdm" },
  { scope: "bilibili", name: "byrecc-bilibili", label: "仅哔哩哔哩", url: "https://api.byre.cc/mcp/bilibili" },
  { scope: "douyin", name: "byrecc-douyin", label: "仅抖音", url: "https://api.byre.cc/mcp/douyin" },
  { scope: "v2ex", name: "byrecc-v2ex", label: "仅 V2EX", url: "https://api.byre.cc/mcp/v2ex" },
  { scope: "xueqiu", name: "byrecc-xueqiu", label: "仅雪球", url: "https://api.byre.cc/mcp/xueqiu" },
  { scope: "reddit", name: "byrecc-reddit", label: "仅 Reddit", url: "https://api.byre.cc/mcp/reddit" },
  { scope: "twitter", name: "byrecc-twitter", label: "仅 Twitter / X", url: "https://api.byre.cc/mcp/twitter" },
  { scope: "xianyu", name: "byrecc-xianyu", label: "仅闲鱼", url: "https://api.byre.cc/mcp/xianyu" },
  { scope: "jd", name: "byrecc-jd", label: "仅京东", url: "https://api.byre.cc/mcp/jd" },
  { scope: "1688", name: "byrecc-1688", label: "仅 1688", url: "https://api.byre.cc/mcp/1688" },
  { scope: "nowcoder", name: "byrecc-nowcoder", label: "仅牛客", url: "https://api.byre.cc/mcp/nowcoder" },
  { scope: "instagram", name: "byrecc-instagram", label: "仅 Instagram", url: "https://api.byre.cc/mcp/instagram" },
  { scope: "maimai", name: "byrecc-maimai", label: "仅脉脉", url: "https://api.byre.cc/mcp/maimai" },
  { scope: "jianyu", name: "byrecc-jianyu", label: "仅剑鱼标讯", url: "https://api.byre.cc/mcp/jianyu" },
  { scope: "toutiao", name: "byrecc-toutiao", label: "仅今日头条", url: "https://api.byre.cc/mcp/toutiao" },
  { scope: "sinafinance", name: "byrecc-sinafinance", label: "仅新浪财经", url: "https://api.byre.cc/mcp/sinafinance" },
  { scope: "juejin", name: "byrecc-juejin", label: "仅掘金", url: "https://api.byre.cc/mcp/juejin" },
  { scope: "36kr", name: "byrecc-36kr", label: "仅 36氪", url: "https://api.byre.cc/mcp/36kr" },
  { scope: "dongchedi", name: "byrecc-dongchedi", label: "仅懂车帝", url: "https://api.byre.cc/mcp/dongchedi" },
  { scope: "ctrip", name: "byrecc-ctrip", label: "仅携程", url: "https://api.byre.cc/mcp/ctrip" },
  { scope: "linkedin", name: "byrecc-linkedin", label: "仅 LinkedIn", url: "https://api.byre.cc/mcp/linkedin" },
  { scope: "github", name: "byrecc-github", label: "仅 GitHub", url: "https://api.byre.cc/mcp/github" },
  { scope: "google", name: "byrecc-google", label: "仅 Google", url: "https://api.byre.cc/mcp/google" },
  { scope: "imdb", name: "byrecc-imdb", label: "仅 IMDb", url: "https://api.byre.cc/mcp/imdb" },
] as const

const mcpSelectOptions = mcpEndpointOptions.map((endpoint) => ({
  value: endpoint.scope,
  label: endpoint.label,
}))
const selectedMcpEndpoint = computed(
  () => mcpEndpointOptions.find((endpoint) => endpoint.scope === mcpScope.value)!,
)

const platformTools = [
  {
    scope: "rednote",
    mark: "RED",
    name: "RedNote",
    localName: "小红书",
    description: "搜索公开笔记、趋势词和城市 Feed，并读取笔记正文与评论。",
    tools: [
      { id: "rednote.search_notes", label: "搜索公开笔记", price: 2 },
      { id: "rednote.trending", label: "获取趋势词", price: 2 },
      { id: "rednote.note_detail", label: "读取笔记详情", price: 1 },
      { id: "rednote.note_comments", label: "读取笔记评论", price: 2 },
      { id: "rednote.local_feed", label: "读取城市笔记", price: 2 },
      { id: "rednote.local_feed_header", label: "解析所在城市", price: 1 },
      { id: "rednote.user_posts", label: "读取用户公开作品", price: 2 },
    ],
  },
  {
    scope: "zhihu",
    mark: "知",
    name: "Zhihu",
    localName: "知乎",
    description: "搜索回答与文章，读取热榜、正文和公开评论。",
    tools: [
      { id: "zhihu.search", label: "搜索回答与文章", price: 2 },
      { id: "zhihu.hot", label: "获取知乎热榜", price: 2 },
      { id: "zhihu.article", label: "读取文章详情", price: 1 },
      { id: "zhihu.answer", label: "读取回答详情", price: 1 },
      { id: "zhihu.comments", label: "读取内容评论", price: 2 },
    ],
  },
  {
    scope: "tophub",
    mark: "TOP",
    name: "TopHub",
    localName: "今日热榜聚合",
    description: "发现实时分类与导航标签，读取跨站榜单资讯。",
    tools: [
      { id: "tophub.categories", label: "获取主导航", price: 1 },
      { id: "tophub.tags", label: "获取分类标签", price: 1 },
      { id: "tophub.items", label: "读取榜单内容", price: 1 },
    ],
  },
  {
    scope: "douban",
    mark: "豆",
    name: "Douban",
    localName: "豆瓣",
    description: "搜索影视、图书等条目，读取详情、长评与短评。",
    tools: [
      { id: "douban.search", label: "搜索豆瓣条目", price: 1 },
      { id: "douban.subject", label: "读取条目详情", price: 1 },
      { id: "douban.reviews", label: "读取条目长评", price: 1 },
      { id: "douban.comments", label: "读取条目短评", price: 1 },
    ],
  },
  {
    scope: "weibo",
    mark: "微",
    name: "Weibo CLI",
    localName: "微博移动接口",
    description: "通过原 weibo-cli 搜索公开微博，读取热搜、正文与评论。",
    tools: [
      { id: "weibo.search", label: "搜索公开微博", price: 1 },
      { id: "weibo.hot", label: "获取微博热搜", price: 1 },
      { id: "weibo.status", label: "读取微博正文", price: 1 },
      { id: "weibo.comments", label: "读取微博评论", price: 1 },
    ],
  },
  {
    scope: "weibo_web",
    mark: "WEB",
    name: "Weibo Web",
    localName: "微博网页接口",
    description: "通过独立 weibo-web-cli 直接 HTTP 搜索和读取微博，热搜无需账号。",
    tools: [
      { id: "weibo_web.search", label: "网页搜索公开微博", price: 1 },
      { id: "weibo_web.hot", label: "网页获取微博热搜", price: 1 },
      { id: "weibo_web.status", label: "网页读取微博正文", price: 1 },
      { id: "weibo_web.comments", label: "网页读取微博评论", price: 1 },
    ],
  },
  {
    scope: "smzdm",
    mark: "值",
    name: "SMZDM",
    localName: "什么值得买",
    description: "搜索好价与社区内容，读取热词、正文和评论。",
    tools: [
      { id: "smzdm.search", label: "搜索好价与文章", price: 1 },
      { id: "smzdm.hot", label: "获取搜索热词", price: 1 },
      { id: "smzdm.content", label: "读取内容摘要", price: 1 },
      { id: "smzdm.detail", label: "读取完整详情", price: 1 },
      { id: "smzdm.comments", label: "读取内容评论", price: 1 },
    ],
  },
  {
    scope: "bilibili",
    mark: "哔",
    name: "Bilibili",
    localName: "哔哩哔哩",
    description: "搜索公开视频，读取热门、排行、视频详情与公开评论。",
    tools: [
      { id: "bilibili.search", label: "搜索公开视频", price: 1 },
      { id: "bilibili.video", label: "读取视频详情", price: 1 },
      { id: "bilibili.hot", label: "获取热门视频", price: 1 },
      { id: "bilibili.rank", label: "获取全站排行", price: 1 },
      { id: "bilibili.comments", label: "读取视频评论", price: 1 },
    ],
  },
  {
    scope: "douyin",
    mark: "抖",
    name: "Douyin",
    localName: "抖音",
    description: "搜索公开作品，读取热榜、作品详情、评论回复和用户公开作品。",
    tools: [
      { id: "douyin.search", label: "搜索公开作品", price: 1 },
      { id: "douyin.detail", label: "读取作品详情", price: 1 },
      { id: "douyin.comments", label: "读取作品评论", price: 1 },
      { id: "douyin.comment_replies", label: "读取评论回复", price: 1 },
      { id: "douyin.hot", label: "获取抖音热榜", price: 1 },
      { id: "douyin.user_posts", label: "读取用户作品", price: 1 },
    ],
  },
  {
    scope: "v2ex", mark: "V2", name: "V2EX", localName: "V2EX",
    description: "读取公开热门、最新、节点主题、主题正文与回复。",
    tools: [
      { id: "v2ex.hot", label: "热门主题", price: 1 },
      { id: "v2ex.latest", label: "最新主题", price: 1 },
      { id: "v2ex.node_topics", label: "节点主题", price: 1 },
      { id: "v2ex.topic", label: "主题详情", price: 1 },
      { id: "v2ex.replies", label: "主题回复", price: 1 },
    ],
  },
  {
    scope: "xueqiu", mark: "雪", name: "Xueqiu", localName: "雪球",
    description: "搜索股票并读取行情、热门讨论和热门股票。",
    tools: [
      { id: "xueqiu.search_stocks", label: "搜索股票", price: 1 },
      { id: "xueqiu.quote", label: "股票行情", price: 1 },
      { id: "xueqiu.hot_posts", label: "热门讨论", price: 1 },
      { id: "xueqiu.hot_stocks", label: "热门股票", price: 1 },
    ],
  },
  {
    scope: "reddit", mark: "R", name: "Reddit", localName: "Reddit",
    description: "搜索并读取公开帖子、社区列表与评论。",
    tools: [
      { id: "reddit.search", label: "搜索帖子", price: 1 },
      { id: "reddit.popular", label: "热门帖子", price: 1 },
      { id: "reddit.subreddit", label: "社区帖子", price: 1 },
      { id: "reddit.detail", label: "帖子详情", price: 1 },
    ],
  },
  {
    scope: "twitter", mark: "X", name: "Twitter / X", localName: "Twitter / X",
    description: "搜索并读取公开帖子及文章。",
    tools: [
      { id: "twitter.search", label: "搜索帖子", price: 1 },
      { id: "twitter.tweet", label: "帖子详情", price: 1 },
      { id: "twitter.article", label: "文章详情", price: 1 },
    ],
  },
  {
    scope: "xianyu", mark: "闲", name: "Xianyu", localName: "闲鱼",
    description: "搜索公开闲置商品，并读取价格、成色、地区、卖家摘要和商品详情。",
    tools: [
      { id: "xianyu.search", label: "搜索闲置商品", price: 1 },
      { id: "xianyu.item", label: "商品详情", price: 1 },
    ],
  },
  {
    scope: "jd", mark: "京", name: "JD", localName: "京东",
    description: "按 SKU 读取公开商品、规格、店铺、库存、服务和图文详情。",
    tools: [
      { id: "jd.search", label: "搜索商品", price: 1 },
      { id: "jd.item", label: "商品详情", price: 1 },
    ],
  },
  {
    scope: "1688", mark: "1688", name: "1688", localName: "阿里巴巴批发网",
    description: "按 Offer ID 读取公开商品详情，或按 Member ID 读取店铺摘要。",
    tools: [
      { id: "1688.item", label: "商品详情", price: 1 },
      { id: "1688.store_summary", label: "店铺摘要", price: 1 },
    ],
  },
  {
    scope: "nowcoder", mark: "牛", name: "Nowcoder", localName: "牛客",
    description: "搜索公开帖子，读取内容详情、面试题库和面试经验。",
    tools: [
      { id: "nowcoder.search", label: "搜索公开内容", price: 1 },
      { id: "nowcoder.detail", label: "读取内容详情", price: 1 },
      { id: "nowcoder.papers", label: "读取面试题库", price: 1 },
      { id: "nowcoder.experience", label: "读取面试经验", price: 1 },
    ],
  },
  {
    scope: "instagram", mark: "IG", name: "Instagram", localName: "Instagram",
    description: "按关键词搜索公开帖子与 Reels，并读取帖子详情。",
    tools: [
      { id: "instagram.search", label: "搜索公开帖子", price: 1 },
      { id: "instagram.post", label: "读取帖子详情", price: 1 },
    ],
  },
  {
    scope: "maimai", mark: "脉", name: "Maimai", localName: "脉脉",
    description: "搜索公开帖子、职位和公司，读取帖子详情与评论；人才搜索只返回匿名职业匹配信息。",
    tools: [
      { id: "maimai.search_posts", label: "搜索公开帖子", price: 1 },
      { id: "maimai.search_jobs", label: "搜索职位", price: 1 },
      { id: "maimai.search_companies", label: "搜索公司", price: 1 },
      { id: "maimai.search_talents", label: "搜索匿名人才匹配", price: 1 },
      { id: "maimai.post", label: "读取帖子详情", price: 1 },
      { id: "maimai.comments", label: "读取帖子评论", price: 1 },
    ],
  },
  {
    scope: "jianyu", mark: "剑", name: "Jianyu", localName: "剑鱼标讯",
    description: "通过登录账号搜索公开招投标与采购公告，并读取公告正文；仅调用 JSON 接口。",
    tools: [
      { id: "jianyu.search", label: "搜索采购公告", price: 1 },
      { id: "jianyu.detail", label: "读取公告详情", price: 1 },
    ],
  },
  {
    scope: "toutiao", mark: "头", name: "Toutiao", localName: "今日头条",
    description: "读取热榜和分类推荐，搜索公开内容，并读取文章、视频或微头条详情。",
    tools: [
      { id: "toutiao.hot", label: "读取热榜", price: 1 },
      { id: "toutiao.recommend", label: "读取分类推荐", price: 1 },
      { id: "toutiao.search", label: "搜索公开内容", price: 1 },
      { id: "toutiao.detail", label: "读取内容详情", price: 1 },
    ],
  },
  {
    scope: "sinafinance", mark: "财", name: "Sina Finance", localName: "新浪财经",
    description: "读取 7x24 与滚动资讯，搜索和查看新闻全文，并查询行情与热门股票。",
    tools: [
      { id: "sinafinance.live_news", label: "读取 7x24 快讯", price: 1 },
      { id: "sinafinance.rolling_news", label: "读取滚动新闻", price: 1 },
      { id: "sinafinance.search", label: "搜索财经新闻", price: 1 },
      { id: "sinafinance.detail", label: "读取新闻详情", price: 1 },
      { id: "sinafinance.quote", label: "查询股票行情", price: 1 },
      { id: "sinafinance.stock_rank", label: "读取热门股票", price: 1 },
    ],
  },
  {
    scope: "juejin", mark: "掘", name: "Juejin", localName: "掘金",
    description: "搜索技术文章、读取完整 Markdown 正文，以及查看热榜和推荐流。",
    tools: [
      { id: "juejin.search", label: "搜索技术文章", price: 1 },
      { id: "juejin.detail", label: "读取文章详情", price: 1 },
      { id: "juejin.hot", label: "读取分类热榜", price: 1 },
      { id: "juejin.recommend", label: "读取推荐文章", price: 1 },
    ],
  },
  {
    scope: "36kr", mark: "36", name: "36Kr", localName: "36氪",
    description: "读取最新资讯、搜索文章、查看完整正文和热门榜单，全程无需登录。",
    tools: [
      { id: "36kr.news", label: "读取最新资讯", price: 1 },
      { id: "36kr.search", label: "搜索文章", price: 1 },
      { id: "36kr.article", label: "读取文章详情", price: 1 },
      { id: "36kr.hot", label: "读取热门榜单", price: 1 },
    ],
  },
  {
    scope: "dongchedi", mark: "车", name: "Dongchedi", localName: "懂车帝",
    description: "搜索车型车系，读取车系详情、在售/停售车型、参数配置、评分和车主口碑。",
    tools: [
      { id: "dongchedi.search", label: "搜索车系", price: 1 },
      { id: "dongchedi.series", label: "读取车系详情", price: 1 },
      { id: "dongchedi.models", label: "读取车型列表", price: 1 },
      { id: "dongchedi.specs", label: "读取参数配置", price: 1 },
      { id: "dongchedi.score", label: "读取车系评分", price: 1 },
      { id: "dongchedi.reviews", label: "读取车主口碑", price: 1 },
    ],
  },
  {
    scope: "ctrip", mark: "旅", name: "Ctrip", localName: "携程",
    description: "搜索旅行内容、酒店与景点，读取酒店点评和详情。机票查询需要已完成短信登录的携程账号；火车票暂不支持。",
    tools: [
      { id: "ctrip.search", label: "搜索旅行内容", price: 1 },
      { id: "ctrip.hotel_suggest", label: "酒店联想", price: 1 },
      { id: "ctrip.hotels", label: "搜索酒店", price: 1 },
      { id: "ctrip.hotel_detail", label: "读取酒店详情", price: 1 },
      { id: "ctrip.flights", label: "搜索机票", price: 1 },
      { id: "ctrip.trains", label: "搜索火车票（暂不可用）", price: 1 },
      { id: "ctrip.attractions", label: "搜索景点", price: 1 },
    ],
  },
  {
    scope: "linkedin", mark: "in", name: "LinkedIn", localName: "LinkedIn",
    description: "使用托管登录账号搜索职位，并读取职位、职业资料、公司与可见帖子。",
    tools: [
      { id: "linkedin.search", label: "搜索职位", price: 1 },
      { id: "linkedin.job_detail", label: "读取职位详情", price: 1 },
      { id: "linkedin.profile", label: "读取职业资料", price: 1 },
      { id: "linkedin.company", label: "读取公司资料", price: 1 },
      { id: "linkedin.posts", label: "读取用户帖子", price: 1 },
    ],
  },
  {
    scope: "github", mark: "GH", name: "GitHub", localName: "GitHub Trending",
    description: "无需登录读取 GitHub Trending 热门仓库，并按时间范围和编程语言筛选。",
    tools: [
      { id: "github.trending_repos", label: "读取热门仓库", price: 1 },
    ],
  },
  {
    scope: "google", mark: "G", name: "Google", localName: "公开搜索 / Feed",
    description: "无需账号搜索 Google 公开网页，或读取 News、搜索联想和每日趋势；不绕过 CAPTCHA。",
    tools: [
      { id: "google.search", label: "搜索公开网页", price: 1 },
      { id: "google.news", label: "搜索新闻或读取头条", price: 1 },
      { id: "google.suggest", label: "读取搜索联想", price: 1 },
      { id: "google.trends", label: "读取每日趋势", price: 1 },
    ],
  },
  {
    scope: "imdb", mark: "IMDb", name: "IMDb", localName: "影视数据库",
    description: "通过 GraphQL 搜索和读取影视、榜单、公众影人与隐私安全的评论。仅限私人非商用。",
    tools: [
      { id: "imdb.search", label: "搜索影视与影人", price: 1 },
      { id: "imdb.title", label: "读取影视详情", price: 1 },
      { id: "imdb.top", label: "读取高分榜", price: 1 },
      { id: "imdb.trending", label: "读取流行榜", price: 1 },
      { id: "imdb.person", label: "读取公众影人", price: 1 },
      { id: "imdb.reviews", label: "读取匿名化评论", price: 1 },
    ],
  },
] as const

const activePlatform = computed(
  () => platformTools.find((platform) => platform.scope === activePlatformScope.value)!,
)
const totalTools = platformTools.reduce((total, platform) => total + platform.tools.length, 0)

const mcpConfig = computed(() =>
  JSON.stringify(
    {
      mcpServers: {
        [selectedMcpEndpoint.value.name]: {
          url: selectedMcpEndpoint.value.url,
          headers: { Authorization: `Bearer ${key.value}` },
        },
      },
    },
    null,
    2,
  ),
)

const restExample = computed(
  () => `curl https://api.byre.cc/v1/content/search \\
  -H 'Authorization: Bearer ${key.value}' \\
  -H 'Content-Type: application/json' \\
  -H 'Idempotency-Key: 01K0YOURUNIQUEKEY' \\
  -d '{"channel":"rednote","phrase":"AI Agent","ranking":"most_liked","freshness":"week"}'`,
)

const currentCode = computed(() => (activeTab.value === "mcp" ? mcpConfig.value : restExample.value))

function usePlatformEndpoint(scope: PlatformScope): void {
  activeTab.value = "mcp"
  mcpScope.value = scope
  window.requestAnimationFrame(() => {
    document.getElementById("integration-title")?.scrollIntoView({ behavior: "smooth", block: "start" })
  })
}

async function copy(): Promise<void> {
  await navigator.clipboard.writeText(currentCode.value)
  copied.value = true
  window.setTimeout(() => (copied.value = false), 1600)
}
</script>

<template>
  <section class="quickstart-page" data-screen-label="Quickstart">
    <header class="quickstart-hero">
      <div class="page-heading page-heading--hero">
        <span class="eyebrow">Integration</span>
        <h1>配置一次，开始调用。</h1>
        <p>一把 API Key 同时连接 MCP 与 REST，在十一个平台之间按需切换。</p>
      </div>
      <div class="quickstart-facts" aria-label="接口概览">
        <div><strong>7</strong><span>内容平台</span></div>
        <div><strong>{{ totalTools }}</strong><span>只读接口</span></div>
        <div><strong>1</strong><span>统一 Key</span></div>
      </div>
    </header>

    <section class="integration-section" aria-labelledby="integration-title">
      <header class="quickstart-section-heading">
        <div>
          <span class="eyebrow">01 · Connect</span>
          <h2 id="integration-title">选择接入方式</h2>
        </div>
        <span>复制配置，即刻连接</span>
      </header>

      <div class="integration-workbench">
        <aside class="integration-control">
          <div class="integration-mode-switch" role="tablist" aria-label="接入方式">
            <button type="button" role="tab" :aria-selected="activeTab === 'mcp'" :class="{ active: activeTab === 'mcp' }" @click="activeTab = 'mcp'">
              <Layers3 :size="17" />
              <span><strong>MCP</strong><small>Agent 与桌面客户端</small></span>
            </button>
            <button type="button" role="tab" :aria-selected="activeTab === 'rest'" :class="{ active: activeTab === 'rest' }" @click="activeTab = 'rest'">
              <Braces :size="17" />
              <span><strong>REST API</strong><small>服务端与自动化脚本</small></span>
            </button>
          </div>

          <div v-if="activeTab === 'mcp'" class="integration-option">
            <span class="integration-option__label">暴露的平台范围</span>
            <AppSelect v-model="mcpScope" aria-label="MCP 平台范围" :options="mcpSelectOptions" />
            <div class="endpoint-preview">
              <span>Endpoint</span>
              <code>{{ selectedMcpEndpoint.url.replace("https://api.byre.cc", "") }}</code>
            </div>
          </div>
          <div v-else class="integration-option">
            <span class="integration-option__label">请求入口</span>
            <div class="endpoint-preview">
              <span>POST</span>
              <code>/v1/content/search</code>
            </div>
            <p>示例使用小红书搜索；其他平台参数可在下方接口目录中查看。</p>
          </div>
        </aside>

        <article class="config-card">
          <header>
            <div><TerminalSquare :size="17" /><span>{{ activeTab === "mcp" ? "mcp.json" : "request.sh" }}</span></div>
            <button class="copy-button copy-button--dark" @click="copy"><Check v-if="copied" :size="14" /><Clipboard v-else :size="14" />{{ copied ? "已复制" : "复制配置" }}</button>
          </header>
          <pre><code>{{ currentCode }}</code></pre>
          <footer v-if="!apiKeys.revealedKey"><span>API Key 仍是占位符，请替换为创建时展示的完整值。</span><RouterLink to="/api-keys">管理 Keys <ExternalLink :size="13" /></RouterLink></footer>
          <footer v-else><span>已自动填入本次会话中刚创建的 Key。</span><span class="safe-label"><Check :size="13" />仅保存在页面内存</span></footer>
        </article>
      </div>
    </section>

    <section class="catalog-section" aria-labelledby="catalog-title">
      <header class="quickstart-section-heading">
        <div>
          <span class="eyebrow">02 · Explore</span>
          <h2 id="catalog-title">按平台浏览接口</h2>
        </div>
        <span>一次只看一个平台，不再平铺全部接口</span>
      </header>

      <div class="tool-explorer">
        <nav class="platform-nav" role="tablist" aria-label="内容平台">
          <button
            v-for="platform in platformTools"
            :key="platform.scope"
            type="button"
            role="tab"
            :aria-selected="activePlatformScope === platform.scope"
            :data-platform="platform.scope"
            :class="{ active: activePlatformScope === platform.scope }"
            @click="activePlatformScope = platform.scope"
          >
            <span class="platform-nav__mark">{{ platform.mark }}</span>
            <span class="platform-nav__name"><strong>{{ platform.name }}</strong><small>{{ platform.localName }}</small></span>
            <span class="platform-nav__count">{{ platform.tools.length }}</span>
          </button>
        </nav>

        <article class="platform-detail" role="tabpanel" :data-platform="activePlatform.scope">
          <header>
            <div>
              <span class="eyebrow">{{ activePlatform.localName }}</span>
              <h3>{{ activePlatform.name }}</h3>
              <p>{{ activePlatform.description }}</p>
            </div>
            <button type="button" class="endpoint-action" @click="usePlatformEndpoint(activePlatform.scope)">
              用于 MCP 配置 <ArrowUpRight :size="14" />
            </button>
          </header>

          <div class="tool-table">
            <div class="tool-table__heading"><span>Operation</span><span>用途</span><span>消耗</span></div>
            <div v-for="tool in activePlatform.tools" :key="tool.id" class="tool-table__row">
              <code><Code2 :size="14" />{{ tool.id }}</code>
              <span>{{ tool.label }}</span>
              <strong>{{ tool.price }} {{ tool.price === 1 ? "credit" : "credits" }}</strong>
            </div>
          </div>
        </article>
      </div>
    </section>
  </section>
</template>

<style scoped>
.quickstart-page { display: grid; gap: 62px; }
.quickstart-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 54px;
  padding-bottom: 34px;
  border-bottom: 1px solid var(--line-strong);
}
.quickstart-hero .page-heading { max-width: 760px; }
.quickstart-hero h1 { max-width: 720px; }
.quickstart-facts { display: grid; grid-template-columns: repeat(3, 96px); border: 1px solid var(--line); background: var(--panel); }
.quickstart-facts div { display: grid; gap: 6px; padding: 16px; border-right: 1px solid var(--line); }
.quickstart-facts div:last-child { border-right: 0; }
.quickstart-facts strong { font: 680 24px/1 monospace; letter-spacing: -.05em; }
.quickstart-facts span { color: var(--muted); font-size: 9px; white-space: nowrap; }

.integration-section, .catalog-section { display: grid; gap: 20px; }
.quickstart-section-heading { display: flex; align-items: end; justify-content: space-between; gap: 30px; }
.quickstart-section-heading h2 { margin: 6px 0 0; font-size: 24px; letter-spacing: -.04em; }
.quickstart-section-heading > span { color: var(--muted); font-size: 10px; }

.integration-workbench {
  display: grid;
  grid-template-columns: 294px minmax(0, 1fr);
  min-height: 390px;
  overflow: hidden;
  border: 1px solid var(--line-strong);
  background: var(--panel);
}
.integration-control { display: grid; align-content: start; gap: 24px; padding: 18px; border-right: 1px solid var(--line); background: var(--panel-muted); }
.integration-mode-switch { display: grid; gap: 7px; }
.integration-mode-switch button {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 68px;
  padding: 12px;
  color: var(--muted);
  border: 1px solid transparent;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.integration-mode-switch button:hover { color: var(--ink); background: color-mix(in oklch, var(--panel) 70%, transparent); }
.integration-mode-switch button.active { color: var(--accent); border-color: var(--line); background: var(--panel); box-shadow: inset 3px 0 var(--accent); }
.integration-mode-switch button > svg { justify-self: center; }
.integration-mode-switch button span { display: grid; gap: 3px; }
.integration-mode-switch strong { color: var(--ink); font-size: 12px; }
.integration-mode-switch small { color: var(--muted); font-size: 9px; }
.integration-option { display: grid; gap: 10px; padding-top: 20px; border-top: 1px solid var(--line); }
.integration-option label, .integration-option__label { color: var(--ink-soft); font-size: 10px; font-weight: 650; }
.integration-option :deep(.app-select) { width: 100%; }
.endpoint-preview { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 42px; padding: 0 11px; border: 1px solid var(--line); background: var(--panel); }
.endpoint-preview span { color: var(--muted); font: 600 8px monospace; text-transform: uppercase; }
.endpoint-preview code { overflow: hidden; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.integration-option p { margin: 1px 0 0; color: var(--muted); font-size: 9px; line-height: 1.7; }

.config-card { min-width: 0; display: grid; grid-template-rows: 58px minmax(0, 1fr) auto; color: oklch(0.9 0.012 148); background: oklch(0.18 0.025 150); }
.config-card > header { display: flex; align-items: center; justify-content: space-between; padding: 0 18px; border-bottom: 1px solid color-mix(in oklch, white 12%, transparent); }
.config-card > header > div { display: flex; align-items: center; gap: 9px; color: color-mix(in oklch, white 67%, transparent); font: 600 10px monospace; }
.config-card .copy-button { color: color-mix(in oklch, white 76%, transparent); border-color: color-mix(in oklch, white 18%, transparent); background: transparent; }
.config-card .copy-button:hover { color: white; border-color: color-mix(in oklch, white 42%, transparent); background: color-mix(in oklch, white 5%, transparent); }
.config-card pre { min-height: 270px; margin: 0; padding: 26px 28px; overflow: auto; white-space: pre-wrap; word-break: break-word; }
.config-card pre code { color: oklch(0.89 0.018 145); font-size: 11px; line-height: 1.85; }
.config-card > footer { min-height: 50px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 18px; border-top: 1px solid color-mix(in oklch, white 12%, transparent); color: color-mix(in oklch, white 48%, transparent); font-size: 9px; }
.config-card > footer a, .config-card .safe-label { display: inline-flex; align-items: center; gap: 5px; color: oklch(0.78 0.12 151); white-space: nowrap; }

.tool-explorer { display: grid; grid-template-columns: 248px minmax(0, 1fr); min-height: 430px; border: 1px solid var(--line-strong); background: var(--panel); }
.platform-nav { display: grid; align-content: start; padding: 10px; border-right: 1px solid var(--line); background: var(--panel-muted); }
.platform-nav button {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 62px;
  padding: 9px 11px;
  color: var(--muted);
  border: 1px solid transparent;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.platform-nav button:hover { color: var(--ink); background: color-mix(in oklch, var(--panel) 65%, transparent); }
.platform-nav button.active { color: var(--ink); border-color: var(--line); background: var(--panel); box-shadow: inset 3px 0 var(--platform-color, var(--accent)); }
.platform-nav__mark { width: 34px; height: 34px; display: grid; place-items: center; color: white; background: var(--platform-color, var(--ink)); font: 700 8px monospace; }
.platform-nav__name { min-width: 0; display: grid; gap: 2px; }
.platform-nav__name strong { overflow: hidden; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.platform-nav__name small { overflow: hidden; color: var(--muted); font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.platform-nav__count { color: var(--faint); font: 600 9px monospace; }
[data-platform="rednote"] { --platform-color: oklch(0.54 0.19 27); }
[data-platform="zhihu"] { --platform-color: oklch(0.49 0.16 250); }
[data-platform="tophub"] { --platform-color: oklch(0.48 0.12 18); }
[data-platform="douban"] { --platform-color: oklch(0.49 0.13 147); }
[data-platform="weibo"] { --platform-color: oklch(0.58 0.17 62); }
[data-platform="weibo_web"] { --platform-color: oklch(0.62 0.18 45); }
[data-platform="smzdm"] { --platform-color: oklch(0.51 0.17 24); }
[data-platform="toutiao"] { --platform-color: oklch(0.58 0.18 28); }
[data-platform="sinafinance"] { --platform-color: oklch(0.54 0.17 45); }
[data-platform="juejin"] { --platform-color: oklch(0.58 0.18 235); }
[data-platform="36kr"] { --platform-color: oklch(0.64 0.18 25); }
[data-platform="dongchedi"] { --platform-color: oklch(0.62 0.19 40); }
[data-platform="ctrip"] { --platform-color: oklch(0.62 0.18 245); }
[data-platform="linkedin"] { --platform-color: oklch(0.55 0.16 250); }
[data-platform="github"] { --platform-color: oklch(0.42 0.04 260); }
[data-platform="google"] { --platform-color: oklch(0.58 0.18 35); }
[data-platform="imdb"] { --platform-color: oklch(0.76 0.16 85); }
[data-platform="bilibili"] { --platform-color: oklch(0.62 0.14 222); }
[data-platform="douyin"] { --platform-color: oklch(0.3 0.05 250); }

.platform-detail { min-width: 0; padding: 30px 34px 34px; }
.platform-detail > header { display: flex; align-items: start; justify-content: space-between; gap: 32px; padding-bottom: 26px; border-bottom: 1px solid var(--line); }
.platform-detail > header h3 { margin: 7px 0 7px; font-size: 30px; line-height: 1; letter-spacing: -.05em; }
.platform-detail > header p { max-width: 510px; margin: 0; color: var(--muted); font-size: 11px; }
.endpoint-action { display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 12px; color: var(--ink-soft); border: 1px solid var(--line-strong); background: transparent; font-size: 10px; font-weight: 650; white-space: nowrap; cursor: pointer; }
.endpoint-action:hover { color: var(--platform-color, var(--accent)); border-color: var(--platform-color, var(--accent)); background: color-mix(in oklch, var(--platform-color, var(--accent)) 5%, transparent); }
.tool-table { display: grid; }
.tool-table__heading, .tool-table__row { display: grid; grid-template-columns: minmax(220px, 1.2fr) minmax(130px, .8fr) 90px; align-items: center; gap: 18px; }
.tool-table__heading { min-height: 42px; color: var(--faint); border-bottom: 1px solid var(--line); font: 600 8px monospace; letter-spacing: .08em; text-transform: uppercase; }
.tool-table__row { min-height: 61px; border-bottom: 1px solid var(--line); }
.tool-table__row code { min-width: 0; display: flex; align-items: center; gap: 9px; overflow: hidden; color: var(--ink); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.tool-table__row code svg { flex: none; color: var(--platform-color, var(--accent)); }
.tool-table__row > span { color: var(--muted); font-size: 10px; }
.tool-table__row strong { justify-self: end; color: var(--ink-soft); font: 600 9px monospace; }

@media (max-width: 980px) {
  .quickstart-hero { grid-template-columns: 1fr; gap: 24px; }
  .quickstart-facts { justify-self: start; }
  .integration-workbench { grid-template-columns: 250px minmax(0, 1fr); }
  .tool-explorer { grid-template-columns: 210px minmax(0, 1fr); }
  .platform-detail { padding-inline: 24px; }
  .tool-table__heading, .tool-table__row { grid-template-columns: minmax(180px, 1.2fr) minmax(110px, .8fr) 76px; }
}

@media (max-width: 760px) {
  .quickstart-page { gap: 48px; }
  .quickstart-section-heading { align-items: start; flex-direction: column; gap: 8px; }
  .integration-workbench, .tool-explorer { grid-template-columns: 1fr; }
  .integration-control { border-right: 0; border-bottom: 1px solid var(--line); }
  .integration-mode-switch { grid-template-columns: 1fr 1fr; }
  .integration-option { max-width: none; }
  .platform-nav { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 5px; border-right: 0; border-bottom: 1px solid var(--line); }
  .platform-nav button { grid-template-columns: 30px minmax(0, 1fr); min-height: 56px; padding: 8px; }
  .platform-nav button.active { box-shadow: inset 0 3px var(--platform-color, var(--accent)); }
  .platform-nav__mark { width: 28px; height: 28px; }
  .platform-nav__count { display: none; }
  .platform-detail { padding: 25px 20px 28px; }
  .tool-table__heading, .tool-table__row { grid-template-columns: minmax(0, 1fr) 74px; gap: 12px; }
  .tool-table__heading span:nth-child(2), .tool-table__row > span { display: none; }
}

@media (max-width: 520px) {
  .quickstart-facts { width: 100%; grid-template-columns: repeat(3, 1fr); }
  .quickstart-facts div { padding: 13px; }
  .integration-mode-switch { grid-template-columns: 1fr; }
  .config-card pre { min-height: 245px; padding: 21px 18px; }
  .config-card > footer { align-items: start; flex-direction: column; }
  .platform-nav { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .platform-detail > header { flex-direction: column; gap: 18px; }
  .endpoint-action { width: 100%; justify-content: center; }
}
</style>
